function subscriptionFrequencyPanelIsTapcartWebview() {
  return (
    typeof window.Tapcart !== 'undefined' ||
    document.documentElement.classList.contains('tapcart-webbridge') ||
    document.body.classList.contains('tapcart-webbridge')
  );
}

class SubscriptionBuilderFrequencyPanel extends HTMLElement {
  constructor() {
    super();
    // Initial state variables
    this.selectedSellingPlanId = null;
    this.selectedPlanText = null;
    this.originalSellingPlanId = null;
    this.handleFrequencyButtonClick = this.handleFrequencyButtonClick.bind(this);
  }

  connectedCallback() {
    this.initializeState();
    this.setupEventListeners();
    this.updateSelectedFrequencyButton();
    this.updateSaveChangesButtonState();
    if (subscriptionFrequencyPanelIsTapcartWebview()) {
      this.dataset.tapcartWebview = 'true';
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  initializeState() {
    if (!this.selectedSellingPlanId) {
      this.originalSellingPlanId = this.getAttribute('selected-selling-plan');
      this.selectedSellingPlanId = this.originalSellingPlanId;
    } else {
      this.originalSellingPlanId = this.selectedSellingPlanId;
    }
  }

  setupEventListeners() {
    // Event listener for the Escape key to close the component
    // this.addEventListener('keyup', this.handleKeyUp.bind(this));

    // Set up overlay click to close the component
    this.overlay = this.querySelector('#SubscriptionBuilderFrequencyPanel-Overlay');
    this.overlay?.addEventListener('click', this.close.bind(this));

    // Set up frequency buttons
    this.frequencyButtons = this.querySelectorAll('.select_frequency_btn');
    this.frequencyButtons.forEach((button) => {
      button.addEventListener('click', this.handleFrequencyButtonClick.bind(this));
    });

    // Set up 'Save Changes' button
    this.saveChangesButton = this.querySelector('.button--primary');
    this.saveChangesButton?.addEventListener('click', this.handleSaveChangesClick.bind(this));

    // Set up close button
    this.closeButton = this.querySelector('.subscription_builder_close');
    this.closeButton?.addEventListener('click', this.close.bind(this));

    // Set up cancel button
    this.cancelButton = this.querySelector('.freq_cancel_btn');
    this.cancelButton?.addEventListener('click', this.close.bind(this));
  }

  removeEventListeners() {
    // Remove overlay click event
    this.overlay?.removeEventListener('click', this.close.bind(this));

    // Remove frequency buttons click events
    this.frequencyButtons.forEach((button) => {
      button.removeEventListener('click', this.handleFrequencyButtonClick.bind(this));
    });

    // Remove 'Save Changes' button click event
    this.saveChangesButton?.removeEventListener('click', this.handleSaveChangesClick.bind(this));

    // Remove close button click event
    this.closeButton?.removeEventListener('click', this.close.bind(this));

    // Remove cancel button click event
    this.cancelButton?.removeEventListener('click', this.close.bind(this));

    // Remove keyup event listener
    // this.removeEventListener('keyup', this.handleKeyUp.bind(this));
  }

  handleKeyUp(event) {
    if (event.code === 'Escape') {
      this.close();
    }
  }

  handleFrequencyButtonClick(event) {
    const button = event.currentTarget;
    const selectedPlanId = button.getAttribute('data-selling-plan-id');
    const btnPlanText = button.querySelector('h3').innerText;
    this.selectedPlanText = btnPlanText;

    // Update selected selling plan ID
    this.selectedSellingPlanId = selectedPlanId;

    // Update UI
    this.updateSelectedFrequencyButton();
    this.updateSaveChangesButtonState();
  }

  updateSelectedFrequencyButton() {
    this.frequencyButtons.forEach((button) => {
      const planId = button.getAttribute('data-selling-plan-id');
      const statusDiv = button.querySelector('.select_frequency_btn_status');
      if (planId == this.selectedSellingPlanId) {
        button.classList.add('active');
        if (statusDiv) {
          statusDiv.textContent = 'Current';
        }
      } else {
        button.classList.remove('active');
        if (statusDiv) {
          statusDiv.textContent = 'Select';
        }
      }
    });
  }

  updateSaveChangesButtonState() {
    if (this.saveChangesButton) {
      if (this.selectedSellingPlanId !== this.originalSellingPlanId) {
        this.saveChangesButton.removeAttribute('disabled');
      } else {
        this.saveChangesButton.setAttribute('disabled', 'disabled');
      }
    }
  }

  handleSaveChangesClick(event) {
    event.preventDefault();

    // Emit an event to the parent component with the new selected selling plan ID
    const frequencyChangedEvent = new CustomEvent('frequencyChanged', {
      detail: {
        selectedSellingPlanId: this.selectedSellingPlanId,
        selectedPlanText: this.selectedPlanText,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(frequencyChangedEvent);

    // Close the panel
    this.close();
  }

  open(triggeredBy, selectedSellingPlanId = null) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    if (selectedSellingPlanId) {
      // Set both selectedSellingPlanId and originalSellingPlanId
      this.selectedSellingPlanId = selectedSellingPlanId;
      this.originalSellingPlanId = selectedSellingPlanId;
    }

    this.classList.add('active');

    // Make the subscription builder's focusable elements inert while this panel is open
    const subscriptionBuilder = document.querySelector('subscription-builder');
    if (subscriptionBuilder) {
      const subscriptionBuilderInner = subscriptionBuilder.querySelector('.drawer__inner');
      if (subscriptionBuilderInner) {
        // Store reference to restore later
        this.subscriptionBuilderInner = subscriptionBuilderInner;
        // Make all focusable elements in the subscription builder non-focusable
        this.subscriptionBuilderFocusableElements = Array.from(
          subscriptionBuilderInner.querySelectorAll(
            'a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:not([type=hidden]):enabled, select:enabled, textarea:enabled'
          )
        );
        this.subscriptionBuilderFocusableElements.forEach((el) => {
          const originalTabIndex = el.getAttribute('tabindex');
          el.setAttribute('data-original-tabindex', originalTabIndex || '');
          el.setAttribute('tabindex', '-1');
        });
      }
    }

    // Trap focus inside the component using the global trapFocus function
    const containerToTrapFocusOn = this.querySelector('.drawer__inner');
    const focusElement =
      containerToTrapFocusOn || this.querySelector('.subscription_builder_close');
    if (typeof trapFocus === 'function' && containerToTrapFocusOn) {
      trapFocus(containerToTrapFocusOn, focusElement);
    }

    document.body.classList.add('overflow-hidden');

    // Update UI based on the selected selling plan
    this.updateSelectedFrequencyButton();
    this.updateSaveChangesButtonState();
  }

  close() {
    this.classList.remove('active');

    // Restore focusability to subscription builder elements
    if (this.subscriptionBuilderFocusableElements) {
      this.subscriptionBuilderFocusableElements.forEach((el) => {
        const originalTabIndex = el.getAttribute('data-original-tabindex');
        if (originalTabIndex === '') {
          el.removeAttribute('tabindex');
        } else if (originalTabIndex) {
          el.setAttribute('tabindex', originalTabIndex);
        } else {
          el.removeAttribute('tabindex');
        }
        el.removeAttribute('data-original-tabindex');
      });
      this.subscriptionBuilderFocusableElements = null;
    }

    // Restore focus trap to subscription builder
    const subscriptionBuilder = document.querySelector('subscription-builder');
    if (subscriptionBuilder && subscriptionBuilder.classList.contains('active')) {
      const containerToTrapFocusOn = subscriptionBuilder.querySelector('.drawer__inner');
      const focusElement =
        containerToTrapFocusOn || subscriptionBuilder.querySelector('.subscription_builder_close');
      if (typeof trapFocus === 'function' && containerToTrapFocusOn) {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }

    // Remove focus trap
    if (typeof removeTrapFocus === 'function' && this.activeElement) {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');

    if (this.activeElement) {
      this.activeElement.focus();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('subscription-builder-frequency-panel', SubscriptionBuilderFrequencyPanel);
